/**
 * "Parfait avec" : ajout direct au panier depuis la fiche produit, sans quitter la page.
 * Reprend le flux d'événements du thème (CartLinesUpdateEvent) pour que le tiroir panier
 * et l'icône panier se mettent à jour exactement comme avec le bouton principal.
 */
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

class WouskaUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('button.wp-up__btn[data-variant-id]');
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;
    this.#add(button);
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const label = button.querySelector('.wp-up__btn-label');
    const original = label?.textContent ?? '';
    button.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const cfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...cfg,
        headers: { ...cfg.headers, Accept: 'text/html' },
      }).then((r) => r.json());

      if (response.status) {
        throw new Error(response.description || response.message || 'Add to cart failed');
      }

      const cart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'wouska-upsell',
          sourceId: variantId,
          itemCount: 1,
          productId: String(response.product_id ?? ''),
          sections: response.sections,
          didError: false,
        },
      });

      button.dataset.added = 'true';
      if (label) label.textContent = 'Ajouté ✓';
      const status = this.querySelector('[data-wp-up-status]');
      if (status) status.textContent = `${button.dataset.productTitle ?? ''} ajouté au panier`;
      setTimeout(() => {
        button.dataset.added = 'false';
        if (label) label.textContent = original;
      }, 2200);
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      button.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Add to cart failed',
          code: 'SERVICE_UNAVAILABLE',
          detail: {},
        })
      );
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('wouska-upsell')) {
  customElements.define('wouska-upsell', WouskaUpsell);
}
